import { z } from "zod";

// ---------------------------------------------------------------------------
// アカウントと氏名（#152、docs/61。2026-10-08 射場さんの決定 ★1〜★3）
//   ・氏名は人（accounts）に1つ。全部の会社で同じ（★1）。所属（users）の姓・名はその写し
//   ・聞くところ：会員登録（★2）／はじめに（/welcome。Google・Apple から届かなかった人・氏名の無い人。★e）／会社を追加（氏名が無い人だけ）／個人設定
//   ・姓・名はどちらも必須（★a）。50文字まで・改行などは入れない（DB の CHECK と同じ）
//   ・「氏名がそろっている」＝姓と名の両方がある。そろっていない人は Me.account.needsName（右上は人の形＋メール、メニューの頭は「氏名登録」）
//   文はここに1か所だけ（画面と API が同じ文を出す）
// ---------------------------------------------------------------------------

export const ACCOUNT_NAME_MSG = {
  family: "姓",
  given: "名",
  familyRequired: "姓を入れてください",
  givenRequired: "名を入れてください",
  /** DB の ACCOUNT_NAME_REQUIRED（姓・名のどちらかが無い） */
  required: "姓と名を入れてください",
  tooLong: "50文字までです",
  badChar: "改行などは入れられません",
  /** DB の ACCOUNT_NAME_INVALID */
  invalid: "姓・名は50文字までです（改行などは入れられません）",
  /** DB の USER_NAME_OWNED（★b：参加した人の氏名は本人だけが直せる） */
  owned: "参加した人の氏名は、本人が個人設定で変えます",
  /** 右上のメニューの頭の行・個人設定の頭（氏名がそろっていないとき） */
  register: "氏名登録",
  /** 会社を追加のシート（氏名が無い人だけ）の節の見出し */
  yourName: "あなたの氏名",
  /** 個人設定の「氏名」のまとまりの注 */
  sameEverywhere: "全部の会社で同じ氏名になります",
  /** 個人設定の節の見出し */
  section: "氏名",
  /** はじめに（/welcome、★e） */
  welcomeTitle: "はじめに",
  welcomeStart: "利用開始",
} as const;

/** アカウントのメニュー・個人設定（#152、docs/61 §2・§5）の文言 */
export const ACCOUNT_MENU_MSG = {
  /** メニュー（スマホ版のシートの頭）・ボタンの読み上げ */
  account: "アカウント",
  close: "閉じる",
  profile: "個人設定",
  plan: "料金プラン",
  logout: "ログアウト",
  /**
   * いまの会社の行の右（会社が2社以上のときだけ「切替 ›」。1社は › だけ）。S-00009 の確かめ（10/11 射場さん）：
   * 前の「ほかに n 社」は会社名に押されて「ほ…」と潰れていた → 「切替」は幅を固定し、会社名の側を「…」で切る
   */
  switchCompany: "切替",
  /** 個人設定の上の帯の右（氏名を直したときだけ押せる。★h） */
  save: "保存",
  saving: "保存しています…",
  /** 個人設定の節の見出し */
  loginSection: "ログイン",
  displaySection: "表示",
  email: "メールアドレス",
  password: "パスワード",
  /** パスワードの行の右（あれば「変更」、無ければ「未設定」） */
  passwordChange: "変更",
  role: "権限",
  /** パスワードの変更のシート（★j） */
  passwordSheetTitle: "パスワードの変更",
  passwordSheetDone: "変更",
  passwordSheetCancel: "取消",
  passwordSheetNote: "変えると、ほかの端末のログインは切れます",
  /** /forgot へのリンク（#287：ログインの画面と同じ言葉＝MSG.forgotLink） */
  forgot: "パスワードをお忘れの方",
  /** 表示のまとまりの行 */
  textSize: "文字サイズ",
  drawerHand: "写真の引き出し",
  view: "表示の形",
} as const;

const NO_CONTROL = /^[^\u0000-\u001f\u007f]*$/;
/** 前後の空白（全角も）を落とす（DB の fn_account_name_clean と同じ） */
const trimName = (v: string) => v.replace(/^[\s　]+|[\s　]+$/g, "");

/** 姓・名の1つ（必須。前後の空白を落とす・50文字まで・改行なし） */
export const namePart = (required: string) => z.string({ error: required }).transform(trimName).pipe(z.string().min(1, required).max(50, ACCOUNT_NAME_MSG.tooLong).regex(NO_CONTROL, ACCOUNT_NAME_MSG.badChar));

/** 姓・名の1つ（任意。会社を追加の口：人に氏名が無いときだけ DB が要ると言う＝ACCOUNT_NAME_REQUIRED） */
export const optionalNamePart = z.string().transform(trimName).pipe(z.string().max(50, ACCOUNT_NAME_MSG.tooLong).regex(NO_CONTROL, ACCOUNT_NAME_MSG.badChar)).optional();

/** 姓・名（どちらも必須）。会員登録・はじめに・個人設定 */
export const PersonName = z.object({
  familyName: namePart(ACCOUNT_NAME_MSG.familyRequired),
  givenName: namePart(ACCOUNT_NAME_MSG.givenRequired),
});
export type PersonName = z.infer<typeof PersonName>;

/** 人の氏名（Me.account・会社を選択の ChooseResponse.account）。needsName＝姓・名がそろっていない（★e：はじめにへ・会社を追加で聞く） */
export const AccountName = z.object({
  familyName: z.string(),
  givenName: z.string(),
  needsName: z.boolean(),
});
export type AccountName = z.infer<typeof AccountName>;

/** 氏名がそろっていないか（API の Me.account.needsName と同じ決まり） */
export const needsName = (v: { familyName: string; givenName: string }): boolean => v.familyName.trim() === "" || v.givenName.trim() === "";

/** 「姓 名」（片方だけならその片方） */
export const fullName = (v: { familyName: string; givenName: string }): string => `${v.familyName} ${v.givenName}`.trim();

/** 人の頭文字（姓の1文字。姓が無ければ名の1文字。どちらも無ければ null＝人の形） */
export const personInitial = (v: { familyName: string; givenName: string }): string | null => [...(v.familyName.trim() || v.givenName.trim())][0] ?? null;
